import { useState } from "react";
import { DrawerForm } from "../../../components/drawers/DrawerForm";
import Icon from "../../../components/base/Icon";
import { useHub } from "../../../core/Global";
import { toastError, toastSuccess } from "../../../components/base/Toast";
import { PriceList } from "../models/PlanCatalog";
import { toDateTime } from "./format";
import TextBox from "../../../components/form/TextBox";


type Key = keyof Omit<PriceList, "modifiedAt">

const groups: { title: string, items: [Key, string, string][] }[] = [
    {
        title: "Add-ons",
        items: [
            ["whatsAppIntegration", "WhatsApp", "On plans that do not include it"],
            ["telegramIntegration", "Telegram", "On plans that do not include it"],
            ["maskedJobCalls", "Masked Job Calls", "One fee for the whole account"]
        ]
    },
    {
        title: "Users and the account",
        items: [
            ["extraUser", "Extra user", "Each, on Pro only (up to 15 users in all)"],
            ["accountHold", "Account hold", "Each month the account is paused"]
        ]
    },
    {
        title: "Phone numbers",
        items: [
            ["localNumberUs", "Local number", "List price — old subscriptions, and a plan with no extra-number price"],
            ["tollFreeNumberUs", "Toll-free number", "List price"]
        ]
    }
]


// The prices that belong to no single plan. Unlike a plan, these are not copied onto what a
// customer bought: add-ons and extra users renew at whatever the list says on the day.
export default function PricesDrawer(props: {
    prices: PriceList,
    onClose: () => void,
    onSaved: () => void
}) {

    const hub = useHub()

    const [values, valuesSet] = useState<Record<Key, string>>(() => {
        const v: any = {}
        groups.forEach(g => g.items.forEach(([k]) => v[k] = String(props.prices[k] ?? 0)))
        return v
    })

    const num = (v: string) => {
        const n = parseFloat((v ?? "").replace(/,/g, ""))
        return isNaN(n) ? 0 : n
    }

    const changed = groups.flatMap(g => g.items)
        .filter(([k]) => num(values[k]) !== (props.prices[k] ?? 0))
        .map(([, label]) => label)

    const save = (callback: any) => {
        const body: any = {}
        groups.forEach(g => g.items.forEach(([k]) => body[k] = num(values[k])))

        hub.Post("/system/plancatalog/prices", body, res => {
            callback(res)
            if (res.success) {
                toastSuccess("General prices saved.")
                props.onSaved()
            }
            else toastError(res.message ?? "Couldn't save the prices.")
        }, () => callback({ success: false }))
    }

    return (
        <DrawerForm id="drawer-plan-prices"
            title="General prices"
            titleIcon="tags"
            subtitle={props.prices.modifiedAt ? "Last changed " + toDateTime(props.prices.modifiedAt) : "Never changed — these are the original prices"}
            size="medium"
            buttonText="Save prices"
            confirmDisabled={changed.length === 0}
            onClose={props.onClose}
            onSubmit={(body, callback) => save(callback)}>

            <div className="catalog-edit">
                {
                    groups.map(g =>
                        <div key={g.title} className="catalog-edit-section">
                            <div className="catalog-edit-title">{g.title}</div>
                            {
                                g.items.map(([k, label, hint]) =>
                                    <div key={k} className="catalog-price-row">
                                        <div>
                                            <div className="fw-semibold">{label}</div>
                                            <div className="text-gray-500 fs-8">{hint}</div>
                                        </div>
                                        <div className="catalog-price-box">
                                            <TextBox id={"price-" + k} name={k} block noMarginBottom money
                                                value={values[k]} onTextChanges={v => valuesSet(x => ({ ...x, [k]: v }))} />
                                        </div>
                                    </div>
                                )
                            }
                        </div>
                    )
                }

                <div className="catalog-edit-summary is-warn">
                    <Icon icon="triangle-exclamation" type="solid" size={14} />
                    <div>
                        These are not held per customer. A new price is charged from each customer's next purchase
                        <b> and next renewal</b> — add-ons and extra users that are already running renew at it, without a notice.
                        Numbers bought on a plan keep the price they were bought at.
                    </div>
                </div>
            </div>

        </DrawerForm>
    )
}
